import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useScrollReveal } from "@/hooks/useScrollReveal";

interface ServiceCardProps {
  imageUrl: string;
  title: string;
  description: string;
  href: string;
  index: number;
  featured?: boolean;
  direction?: "up" | "left" | "right";
  imageAlt?: string;
}

export function ServiceCard({
  imageUrl,
  title,
  description,
  href,
  index,
  featured,
  direction = "up",
  imageAlt,
}: ServiceCardProps) {
  const { ref, style } = useScrollReveal<HTMLAnchorElement>({
    delay: index * 80,
    direction,
  });

  return (
    <Link
      ref={ref}
      to={href}
      style={style}
      className={cn(
        "group glass-card rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-300",
        "hover:scale-[1.03] hover:-translate-y-2 hover:shadow-[0_20px_40px_-12px_hsl(var(--primary)/0.15)]",
        "hover:border-primary/20",
        featured && "col-span-2"
      )}
    >
      <div className="h-32 sm:h-40 overflow-hidden relative">
        <img
          src={imageUrl}
          alt={imageAlt || title}
          decoding="async"
          loading="lazy"
          fetchPriority="low"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300" />
      </div>

      <div className="p-5 sm:p-7">
        <h3 className="font-display text-base font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-200">
          {title}
        </h3>

        <p className="text-muted-foreground text-sm leading-relaxed mb-3">
          {description}
        </p>

        <span className="text-primary text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2.5 transition-all duration-300">
          Mehr erfahren
          <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </span>
      </div>
    </Link>
  );
}
